<template>
  <div>
    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
    <label
      v-if="label != null && label.length > 0"
      :for="componentId"
      class="input-select__label fs-tiny"
    >
      <strong>{{ label }}</strong>
    </label>
    <div
      ref="selectContainer"
      class="input-select position-relative d-flex"
      :class="{
        'input-select--disabled': disabled,
      }"
    >
      <div
        class="input-select__select d-flex w-100 border-bottom border-rhino"
        :class="{
          'input-select__select--open border-bottom-0 rounded-top shadow':
            isResultListOpen,
          'cursor-pointer': !isResultListOpen,
        }"
      >
        <div
          v-if="!isResultListOpen"
          class="input-select__value w-100 lh-1 col ps-4 align-items-center"
          @click="onOpenResultList"
        >
          {{ internalPlaceholder }}

          <div class="input-select__hint">
            <fa-icon
              v-if="!disabled && !isValid"
              class="input-select__hint-icon input-select__hint-icon--danger"
              :icon="faInfoCircle"
              :size="resultListIconSize"
            />
          </div>
        </div>
        <input
          :id="componentId"
          ref="filterInput"
          v-model="searchQuery"
          class="form-control col input-select__input bg-transparent px-4 py-1"
          :class="{
            'position-absolute input-select__input': !isResultListOpen,
            'input-select__input--active': isResultListOpen,
          }"
          type="text"
          :placeholder="internalPlaceholder"
          autocomplete="off"
          :style="{ opacity: isResultListOpen ? '1' : '0' }"
          @input="onSearchInput"
          @focus="onOpenResultList"
          @blur="onInputBlur"
        />

        <fa-icon
          v-if="!disabled"
          class="input-select__select-toggle p-2 cursor-pointer"
          :icon="isResultListOpen ? faAngleUp : faAngleDown"
          :size="resultListIconSize"
          tabindex="-1"
          @click.prevent="toggleSelectOpen"
        />
      </div>
      <div
        v-if="isResultListOpen"
        class="input-select__results-inner position-absolute w-100 overflow-hidden rounded-bottom top-100"
      >
        <div
          v-if="isResultsEmpty"
          class="input-select__results-item py-2 px-4 input-select__results-empty"
        >
          {{
            noEntryTitle.length > 0
              ? noEntryTitle
              : t('general/messages/nothing_found')
          }}
        </div>
        <ul
          v-else
          class="input-select__results position-relative start-0"
          tabindex="-1"
        >
          <!-- grouped mode -->
          <template v-if="grouped">
            <template v-for="(filterItem, index) in filteredItems">
              <li
                v-if="getGroupItems(filterItem).length > 0"
                :key="`group__${filterItem[groupKey]}`"
              >
                <ul class="filter__results-group">
                  <!-- group header -->
                  <li
                    class="input-select__results-item d-block py-2 px-4 text-secondary text-uppercase"
                  >
                    <strong>{{ getGroupLabel(filterItem) }}</strong>
                  </li>
                  <!-- value -->
                  <form-select-item
                    v-for="groupItem in getGroupItems(filterItem)"
                    :key="groupItem[valueKey].toString()"
                    :item="groupItem"
                    :item-label-item="itemLabelFn"
                    :is-selected="isItemSelected(groupItem)"
                    :is-focused="index === itemFocusIndex"
                    :label-key="labelKey"
                    @item-selected="(item) => onItemSelected(item)"
                  />
                </ul>
              </li>
            </template>
          </template>
          <!-- ungrouped mode -->
          <template v-else>
            <form-select-item
              v-for="(filterItem, index) in filteredItems"
              :key="filterItem[valueKey].toString()"
              :item="filterItem"
              :label-key="labelKey"
              :item-label-item="itemLabelFn"
              :is-selected="isItemSelected(filterItem)"
              :is-focused="index === itemFocusIndex"
              @item-selected="(item) => onItemSelected(item)"
            />
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  faAngleDown,
  faAngleUp,
  faInfoCircle,
} from '@fortawesome/pro-solid-svg-icons';
import {
  onClickOutside,
  onKeyStroke,
  useElementBounding,
  useMagicKeys,
} from '@vueuse/core';
import useItemLabel from './composables/useItemLabel';
import type {
  FilterSelectItemsFn,
  SelectItemLabelFn,
  SelectValueType,
} from '~/components/form/select/types';

export default defineComponent({
  props: {
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    label: {
      type: String,
      default: '',
    },
    modelValue: {
      type: [String, Number, Object] as PropType<SelectValueType>,
      required: false,
      default: null,
    },
    query: {
      type: String,
      required: false,
      default: '',
    },
    resultListIconSize: {
      type: String,
      default: 'lg',
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    data: {
      type: Array as PropType<Array<Record<string, unknown>>>,
      required: true,
      default: () => [],
    },
    valueKey: {
      type: String,
      required: false,
      default: 'key',
    },
    labelKey: {
      type: String,
      required: false,
      default: 'label',
    },
    nullable: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemLabelFn: {
      type: Function as PropType<SelectItemLabelFn>,
      required: false,
      default: null,
    },
    /**
     * TODO: revisit grouped mode!
     */
    grouped: {
      type: Boolean,
      required: false,
      default: false,
    },
    groupKey: {
      type: String,
      required: false,
      default: 'key',
    },
    groupLabelKey: {
      type: String,
      required: false,
      default: 'label',
    },
    groupitemLabelFn: {
      type: Function as PropType<SelectItemLabelFn>,
      required: false,
      default: null,
    },
    groupItemsKey: {
      type: String,
      required: false,
      default: 'items',
    },
    filterItemsFn: {
      type: Function as PropType<FilterSelectItemsFn>,
      required: false,
      default: null,
    },
    isValid: {
      type: Boolean,
      default: true,
    },
    icon: {
      type: Array,
      required: false,
      default: undefined,
    },
    noEntryTitle: {
      type: String,
      required: false,
      default: '',
    },
    inputId: {
      type: String,
      required: true,
    },
  },
  emits: ['update:modelValue', 'queryChanged'],
  setup: (props, { emit }) => {
    const searchQuery = ref(props.query);
    const isResultListOpen = ref(false);
    const internalValue = ref(props.modelValue);

    // DOM stuff
    const filterInput = ref<HTMLInputElement>();
    const selectContainer = ref<HTMLDivElement>();
    const componentId = useId();

    // usability stuff
    const itemFocusIndex = ref<number>(-1);

    // validate data array
    const validatePropsData = () => {
      if (
        !props.grouped &&
        props.data.length > 0 &&
        props.data.every((item) => item[props.valueKey] === undefined)
      ) {
        throw new Error(
          `At least one element of your data must have the ${props.valueKey} property.`,
        );
      }
    };

    validatePropsData();

    const filteredItems = ref(props.data);

    const filterItemsByQuery = async (): Promise<void> => {
      if (props.filterItemsFn != null) {
        filteredItems.value = await props.filterItemsFn(
          props.data,
          searchQuery.value,
        );
      } else {
        filteredItems.value = props.data;
      }

      itemFocusIndex.value = -1;
    };

    const getItemLabel = useItemLabel(props.labelKey, props.itemLabelFn);

    const getInternalValueLabel = () => {
      if (typeof internalValue.value === 'object') {
        return getItemLabel(internalValue.value);
      }

      if (
        typeof internalValue.value === 'string' ||
        typeof internalValue.value === 'number'
      ) {
        const labelledItem = props.data.find(
          (item) => item[props.valueKey] === internalValue.value.toString(),
        );
        return getItemLabel(labelledItem);
      }

      return '';
    };

    const setValue = (newValue: SelectValueType) => {
      internalValue.value = newValue;
      if (newValue == null) {
        searchQuery.value = '';
      } else {
        searchQuery.value = getInternalValueLabel();
      }
    };

    const onItemSelected = (filterItem) => {
      if (
        internalValue.value != null &&
        filterItem[props.valueKey] === internalValue.value[props.valueKey] &&
        props.nullable
      ) {
        internalValue.value = null;
      }

      setValue(filterItem);

      // respect original typing of value prop
      if (typeof props.modelValue === 'object') {
        emit('update:modelValue', filterItem);
      } else {
        emit('update:modelValue', filterItem[props.valueKey]);
      }
      isResultListOpen.value = false;
    };

    // when outside value changes -> set internal value and update search query
    watch(
      () => props.modelValue,
      (newValue) => {
        // force setting when value is null -> preselect first element instead
        if (!props.nullable && newValue == null) {
          setValue(props.data[0]);
        }

        setValue(newValue);
      },
    );

    watch(
      () => props.data,
      () => {
        validatePropsData();

        // do not change value in search mode and our select is nullable
        // and its value is null
        if (props.nullable && internalValue.value == null) return;

        // force setting value to null when data source is non existent
        if (props.data.length === 0) {
          setValue(null);
          emit('update:modelValue', null);
        }

        filterItemsByQuery();
      },
    );

    watch(
      () => props.query,
      () => {
        searchQuery.value = props.query;

        filterItemsByQuery();
      },
    );

    watch(() => props.valueKey, validatePropsData);

    const getGroupLabel = (group: Record<string, unknown>) => {
      if (group == null) return '';
      if (props.groupitemLabelFn != null)
        return props.groupitemLabelFn.call(null, group);
      return group[props.groupLabelKey];
    };

    const onOpenResultList = async () => {
      if (props.disabled) return;

      if (!isResultListOpen.value) {
        searchQuery.value = '';
        isResultListOpen.value = true;
        filterItemsByQuery();

        await nextTick();
        filterInput.value.focus();
        itemFocusIndex.value = -1;
      }
    };

    const onCloseResultList = () => {
      const internalValueLabel = getInternalValueLabel();
      if (searchQuery.value != internalValueLabel) {
        searchQuery.value = internalValueLabel;
      }

      isResultListOpen.value = false;
      itemFocusIndex.value = -1;
      filterInput.value.blur();
    };

    const onClearQuery = () => {
      if (props.nullable) {
        internalValue.value = null;
      }

      searchQuery.value = '';
      emit('queryChanged', '');
      filterItemsByQuery();
    };

    const onSearchInput = async () => {
      isResultListOpen.value = true;
      emit('queryChanged', searchQuery.value);
      filterItemsByQuery();
    };

    const isItemSelected = (filterItem: Record<string, unknown>) => {
      if (internalValue.value == null) return false;
      return filterItem[props.valueKey] === internalValue.value[props.valueKey];
    };

    const getGroupItems = (group) =>
      group[props.groupItemsKey] as Array<Record<string, unknown>>;

    const internalPlaceholder = computed(() =>
      internalValue.value == null ? props.placeholder : getInternalValueLabel(),
    );

    const isResultsEmpty = computed(
      () =>
        filteredItems.value.length == 0 ||
        (props.grouped &&
          filteredItems.value.every(
            (group) => getGroupItems(group).length === 0,
          )),
    );

    // handle focus item with keyboard
    const scrollInResultContainer = () => {
      const focusedItemEl = selectContainer.value.querySelectorAll(
        '.input-select__results-item',
      )[itemFocusIndex.value] as HTMLElement;
      const { height: selectItemHeight } = useElementBounding(focusedItemEl);
      const selectContainerEl = selectContainer.value.querySelector(
        '.input-select__results',
      );

      selectContainer.value.querySelector('.input-select__results').scrollTo({
        top:
          focusedItemEl.offsetTop -
          selectContainerEl.clientHeight +
          selectItemHeight.value,
      });
    };

    onKeyStroke('ArrowDown', (event) => {
      if (isResultListOpen.value) {
        event.preventDefault();

        if (itemFocusIndex.value < filteredItems.value.length - 1) {
          itemFocusIndex.value += 1;
        } else if (itemFocusIndex.value === filteredItems.value.length - 1) {
          itemFocusIndex.value = filteredItems.value.length - 1;
        }

        scrollInResultContainer();
      }
    });

    onKeyStroke('ArrowUp', (event) => {
      if (isResultListOpen.value) {
        event.preventDefault();

        if (itemFocusIndex.value > 0) {
          itemFocusIndex.value -= 1;
        } else if (itemFocusIndex.value === -1) {
          itemFocusIndex.value = 0;
        }

        scrollInResultContainer();
      }
    });

    const { enter } = useMagicKeys({
      passive: false,
      onEventFired: (e) => {
        // prevent falsely firing submit when in form
        if (
          e.key === 'Enter' &&
          isResultListOpen.value &&
          itemFocusIndex.value >= 0
        ) {
          e.preventDefault();
        }
      },
    });

    watch(
      () => enter.value,
      (newValue) => {
        if (newValue && isResultListOpen.value) {
          // is new value
          if (
            itemFocusIndex.value >= 0 &&
            filteredItems.value[itemFocusIndex.value] != null
          ) {
            onItemSelected(filteredItems.value[itemFocusIndex.value]);
          }

          // is current value - we have to force closing the result container
          if (
            itemFocusIndex.value >= -1 &&
            filteredItems.value.length === 1 &&
            filteredItems.value[0][props.valueKey] ===
              internalValue.value[props.valueKey]
          ) {
            onItemSelected(filteredItems.value[0]);
          }
        }
      },
    );

    const onInputBlur = (event: FocusEvent) => {
      if (
        event.relatedTarget != null &&
        ((event.relatedTarget as HTMLElement).classList.contains(
          'input-select__results',
        ) ||
          (event.relatedTarget as HTMLElement).classList.contains(
            'input-select__select-toggle',
          ))
      ) {
        return;
      }

      if (isResultListOpen.value) {
        onCloseResultList();
      }
    };

    onClickOutside(selectContainer, () => {
      onCloseResultList();
    });

    const toggleSelectOpen = (event: PointerEvent) => {
      event.preventDefault();
      // avoid open when enter is pressed
      if (event.pointerType != null && event.pointerType.length === 0) return;

      if (isResultListOpen.value) {
        onCloseResultList();
      } else {
        onOpenResultList();
      }
    };

    const { t } = useI18n();

    return {
      faAngleDown,
      faAngleUp,
      faInfoCircle,

      t,

      componentId,
      selectContainer,
      filterInput,
      isResultListOpen,
      itemFocusIndex,
      searchQuery,
      internalValue,
      filteredItems,
      internalPlaceholder,
      isResultsEmpty,
      getItemLabel,
      getGroupLabel,
      isItemSelected,
      getGroupItems,
      toggleSelectOpen,
      onOpenResultList,
      onCloseResultList,
      onItemSelected,
      onClearQuery,
      onSearchInput,
      onInputBlur,
    };
  },
});
</script>

<style lang="scss" scoped>
@import 'gportal-theme/scss/colors';
.input-select {
  $self: &;

  &__label {
    &::first-letter {
      text-transform: capitalize;
    }
  }

  &__select {
    &--open {
      background-color: $gp-midnight-2;
    }
  }

  &__results {
    max-height: 10rem;
    overflow-y: auto;

    &-inner {
      background-color: $gp-midnight-2;

      &:before {
        content: '';
        width: 98%;
        height: 1px;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        background-color: $gp-mouse;
      }
    }
  }

  &__results {
    &-inner {
      z-index: 1;
      box-shadow: 3px 3px 5px 0 rgba(0, 0, 0, 0.3);
    }
  }

  &__value {
    display: flex;

    &:hover {
      cursor: pointer;
    }
  }

  &__hint {
    &-icon {
      &--danger {
        color: $danger;
      }
    }
  }

  &__input {
    z-index: -1;

    &--active {
      z-index: unset;
    }
  }

  &--disabled {
    color: $gp-mouse;

    #{ $self }__value {
      &:hover {
        cursor: default !important;
      }
    }
  }

  &--search {
    .input-select {
      &__results-item {
        &:hover {
          background: none;
          cursor: default;
        }
      }
    }
  }
}
</style>
